import type { ReactNode } from 'react';
import { create } from 'zustand';

/**
 * The shared bottom toast slot (YAR-476, YAR-392/YAR-477).
 *
 * One slot at the bottom of the screen, two lanes feeding it:
 *
 * - `action` — a toast the parent can act on (today: Undo after completing a
 *   task). It TAKES the slot: while it is up, whatever the status lane holds
 *   is hidden, not stacked.
 * - `status` — the email status toast (YAR-477). It shows whenever the action
 *   lane is empty, and comes back on its own once the action toast ends.
 *
 * Never stacked: exactly one entry is visible at a time (`action ?? status`).
 *
 * The clock counts VISIBLE time. `BottomToastSlot` runs it and pauses it on
 * hover, on keyboard focus inside the toast, and while any overlay (Modal,
 * FullScreenSheet, the mobile drawer) is open — see `overlayStore`. A hidden
 * status toast keeps its remaining time and resumes from there when it
 * returns. An action toast may also carry `expiresAt`, a wall-clock
 * deadline the slot enforces even while that clock is paused.
 *
 * ── Status lane API (for the email status toast) ────────────────────────
 *
 *   const { showStatus, dismissStatus } = useBottomToastStore.getState();
 *   showStatus({
 *     id: 'email-status',          // stable, caller-owned
 *     announcement: 'Reading 1 email',   // what screen readers hear
 *     render: () => <MyStatusToast … />, // the visual, full slot width
 *     durationMs: null,            // null = stays until dismissStatus()
 *   });
 *   // Calling showStatus again with the SAME id updates it in place and
 *   // keeps its remaining time — including the time the slot's running
 *   // clock has already used — unless you pass a durationMs, which is the
 *   // explicit way to re-arm the clock.
 *   dismissStatus('email-status');
 *
 * `render` output is placed inside the slot's positioned container; the slot
 * owns placement, width, z-index, motion and the polite live region, so a
 * status toast only draws its own card.
 */

export type BottomToastLane = 'action' | 'status';

export type BottomToastEndReason =
  /** The visible-time clock ran out. */
  | 'timeout'
  /** The parent tapped ✕. */
  | 'close'
  /** The parent tapped the action (Undo). */
  | 'action'
  /** A newer toast of the same lane took its place. */
  | 'replaced'
  /** The parent left the screen the toast was raised on. */
  | 'route'
  /** Dismissed programmatically. */
  | 'dismissed';

/** Undo toast after completing a task — the only action toast today. */
export const UNDO_TOAST_DURATION_MS = 6000;

export interface ActionToastInput {
  /** Leading text that hugs, then truncates on one line (the task name). */
  name: string;
  /** Trailing text that never truncates ("completed"). */
  suffix: string;
  /** The action button label ("Undo"). */
  actionLabel: string;
  onAction: () => void;
  /** Show the ✕ button. Defaults to true. */
  closeable?: boolean;
  /** Visible time before it ends. Defaults to {@link UNDO_TOAST_DURATION_MS}. */
  durationMs?: number;
  /** Wall-clock deadline (epoch ms). The slot ends the toast at it whatever the visible-time clock says — paused, hidden or not. */
  expiresAt?: number;
  /** Called exactly once when the toast leaves the slot, with why. */
  onEnd?: (reason: BottomToastEndReason) => void;
}

export interface ActionToastEntry extends ActionToastInput {
  id: string;
  /** Path the toast was raised on; leaving it ends the toast. */
  originPath: string;
  remainingMs: number;
  /** Bumped on every show; restarts the slot's clock. */
  revision: number;
}

export interface StatusToastInput {
  id: string;
  announcement: string;
  render: () => ReactNode;
  /**
   * Visible time before it ends; `null` keeps it until dismissed. On a
   * re-show of the same id, passing it re-arms the clock; omitting it keeps
   * the time left.
   */
  durationMs?: number | null;
  onEnd?: (reason: BottomToastEndReason) => void;
}

export interface StatusToastEntry extends Omit<StatusToastInput, 'durationMs'> {
  remainingMs: number | null;
  /**
   * Bumped when the clock must re-arm: a new toast or a new `durationMs`. An in-place update (same id, new render/announcement) keeps
   * it, so the slot's running clock is not interrupted and no elapsed
   * visible time is lost.
   */
  revision: number;
}

interface BottomToastState {
  action: ActionToastEntry | null;
  status: StatusToastEntry | null;
}

interface BottomToastActions {
  /** Raise an action toast. Replaces (and ends) any current one. */
  showAction: (input: ActionToastInput) => string;
  /**
   * End the action toast. With `id`, only if it is still that toast, so a
   * stale callback cannot end a newer one.
   */
  dismissAction: (id?: string, reason?: BottomToastEndReason) => void;
  showStatus: (input: StatusToastInput) => void;
  dismissStatus: (id?: string, reason?: BottomToastEndReason) => void;
  /**
   * Record how much visible time a lane's entry has left. Slot-internal.
   * Ignored unless `revision` still matches, so a clock stopping for a
   * re-show cannot overwrite the duration the re-show just set.
   */
  setRemaining: (
    lane: BottomToastLane,
    id: string,
    revision: number,
    remainingMs: number,
  ) => void;
}

function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

let revision = 0;

function currentPath() {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

export const useBottomToastStore = create<
  BottomToastState & BottomToastActions
>()((set, get) => ({
  action: null,
  status: null,

  showAction: (input) => {
    const previous = get().action;
    const id = newId();
    set({
      action: {
        ...input,
        id,
        originPath: currentPath(),
        remainingMs: input.durationMs ?? UNDO_TOAST_DURATION_MS,
        revision: ++revision,
      },
    });
    previous?.onEnd?.('replaced');
    return id;
  },

  dismissAction: (id, reason = 'dismissed') => {
    const current = get().action;
    if (!current || (id && current.id !== id)) return;
    set({ action: null });
    current.onEnd?.(reason);
  },

  showStatus: (input) => {
    const previous = get().status;
    const sameToast = previous?.id === input.id;
    // A same-id re-show without a duration is an in-place update (every
    // realtime change re-renders the email status toast). It keeps the revision, so the slot's clock — keyed on
    // the revision — keeps running instead of restarting from the last
    // remainingMs it wrote back, which would silently hand the toast back
    // all the time it had already been visible.
    if (sameToast && input.durationMs === undefined) {
      set({
        status: {
          ...previous,
          announcement: input.announcement,
          render: input.render,
          onEnd: input.onEnd,
        },
      });
      return;
    }
    set({
      status: {
        id: input.id,
        announcement: input.announcement,
        render: input.render,
        onEnd: input.onEnd,
        remainingMs: input.durationMs ?? null,
        revision: ++revision,
      },
    });
    if (previous && !sameToast) previous.onEnd?.('replaced');
  },

  dismissStatus: (id, reason = 'dismissed') => {
    const current = get().status;
    if (!current || (id && current.id !== id)) return;
    set({ status: null });
    current.onEnd?.(reason);
  },

  setRemaining: (lane, id, rev, remainingMs) => {
    if (lane === 'action') {
      const action = get().action;
      if (action?.id === id && action.revision === rev) {
        set({ action: { ...action, remainingMs } });
      }
      return;
    }
    const status = get().status;
    if (status?.id === id && status.revision === rev) {
      set({ status: { ...status, remainingMs } });
    }
  },
}));
